import { type GiftCardPurchaseFormData } from '@/features/cms/components/CMS/types'
import { RadioButton } from '@shipt/design-system-radio-group'
import { Tile, TileGroup, type TileProps } from '@shipt/design-system-tile'
import { Image } from '@/features/shared/elements/Image/NextImage'
import styled from 'styled-components'
import { type ChangeEvent } from 'react'
import { Column } from '@shipt/design-system-layouts'
import { Headline } from '@shipt/design-system-typography'
import { Carousel } from '@/features/shop/components/Carousel'
import { screenSizes } from '@shipt/design-system-themes'
import { type GiftCardFormInput } from '@/features/cms/components/CMS/components/GiftCardPurchaseForm/GiftCardRecipientForm'
import { useWatch, type UseFormRegister, type UseFormReturn } from 'react-hook-form'

type TileRadioButtonProps = Parameters<TileProps['inputElement']>[0]

const getTileRadioButton =
  (
    content: {
      content_type_id: 'gift_card_image'
      data: GiftCardPurchaseFormData['data']['gift_cards'][0]['data']
    },
    handleChange: () => void,
    register: UseFormRegister<GiftCardFormInput>,
  ) =>
  ({ onChange, ...inputProps }: TileRadioButtonProps) => {
    const handleRadioChange = (e: ChangeEvent<HTMLInputElement>) => {
      handleChange()
      onChange(e)
    }

    return (
      <RadioButton
        {...register('giftImage')}
        {...inputProps}
        onChange={handleRadioChange}
        aria-label={content.data.image.alt}
      />
    )
  }

type Props = {
  designImages: GiftCardPurchaseFormData['data']['gift_cards']
  giftCardForm: UseFormReturn<GiftCardFormInput, object>
}

export const GiftDesignChoices = ({ designImages, giftCardForm }: Props) => {
  const { setValue, register, control } = giftCardForm

  const watchGiftImageValues = useWatch({
    control,
    name: ['giftImage.src', 'giftImage.alt'],
  })
  const handleChoice =
    (content: {
      content_type_id: 'gift_card_image'
      data: GiftCardPurchaseFormData['data']['gift_cards'][0]['data']
    }) =>
    () => {
      setValue('giftImage.src', content.data.image.src)
      setValue('giftImage.alt', content.data.image.alt)
    }

  // keep img sizes identical to prevent multiple image fetches
  const imgHeight = 196
  const imgWidth = 343

  return (
    <DesignWrapper spacing="md" align={{ md: 'center' }} padding={{ base: [0, 'xl'], md: [0] }}>
      <Headline as="h2" size="md">
        Choose Design
      </Headline>
      <Column align={{ md: 'center' }} spacing="3xl">
        <TileGroup canMultiSelect={false} name="DesignChoices">
          <TileWrapper>
            {designImages.map((content, i) => {
              return (
                <ImageTile
                  size="sm"
                  defaultChecked={i === 0}
                  data-is-selected={content.data.image.src === watchGiftImageValues[0]}
                  key={content.data.image.alt}
                  hasHiddenInput
                  onClick={() => {
                    setValue('giftImage.src', content.data.image.src)
                    setValue('giftImage.alt', content.data.image.alt)
                  }}
                  inputElement={getTileRadioButton(content, handleChoice(content), register)}
                >
                  <GridImage
                    height={imgHeight}
                    width={imgWidth}
                    src={content.data.image.src}
                    alt={content.data.image.alt}
                  />
                </ImageTile>
              )
            })}
          </TileWrapper>
        </TileGroup>
        <TileGroup name="DesignChoices" canMultiSelect={false}>
          <TabletPlusView>
            <Carousel spaceBetween={8} simulateTouch scrollOneCard>
              {designImages.map((content, i) => {
                return (
                  <ImageTile
                    size="sm"
                    defaultChecked={i === 0}
                    data-is-selected={content.data.image.src === watchGiftImageValues[0]}
                    key={content.data.image.alt}
                    hasHiddenInput
                    onClick={() => {
                      setValue('giftImage.src', content.data.image.src)
                      setValue('giftImage.alt', content.data.image.alt)
                    }}
                    inputElement={getTileRadioButton(content, handleChoice(content), register)}
                  >
                    <CarouselImage
                      height={imgHeight}
                      width={imgWidth}
                      src={content.data.image.src}
                      alt={content.data.image.alt}
                    />
                  </ImageTile>
                )
              })}
            </Carousel>
          </TabletPlusView>
        </TileGroup>
        <SelectedImage
          src={watchGiftImageValues[0]}
          alt={watchGiftImageValues[1]}
          height={imgHeight}
          width={imgWidth}
        />
      </Column>
    </DesignWrapper>
  )
}

const DesignWrapper = styled(Column)`
  align-self: stretch;
`

const GridImage = styled(Image)`
  display: block;
  width: 100%;
  height: auto;
`
const CarouselImage = styled(Image)`
  display: block;
  max-height: 96px;
  max-width: 167px;
`
const SelectedImage = styled(Image)`
  border-radius: 1rem;
  align-self: center;
`

const ImageTile = styled(Tile)<{ 'data-is-selected': boolean }>`
  overflow: hidden;
  padding: 0;
  cursor: pointer;

  /* This prevents layout shift when changing options */
  border-width: 2px;

  /* Tile has a border by default */
  &[data-is-selected='false'] {
    border-color: transparent;
  }
`

const TileWrapper = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(155px, 1fr));
  gap: 0.5rem;

  @media ${screenSizes.tablet} {
    display: none;
  }
`

const TabletPlusView = styled.div`
  position: relative;
  max-width: 50rem;
  display: none;

  @media ${screenSizes.tablet} {
    display: flex;
  }
`
